// اجزای مشترک صفحات ساختمان‌ها، منصوبات، قبوض، بیمه و تعمیرات
import React, { useState, useEffect, useRef } from 'react';
import { ui } from '../../components/ListPage';
import { openReport } from '../../components/report';
import { jsonOf, netErr } from '../../utils/http';

export const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
export const toEng = s => String(s ?? '').replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
export const num = v => { const n = Number(toEng(String(v ?? '')).replace(/,/g, '')); return isNaN(n) ? 0 : n; };
export const fmtAmt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');
export const fmtCnt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');

export const curYear = () => {
  const y = new Date().toLocaleDateString('fa-IR-u-nu-latn').split('/')[0];
  return parseInt(y, 10) || 1405;
};

export const API = (path, opts = {}) => fetch(`/api/tadarokat/buildings${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

export const FACILITIES_ITEMS = ['آب', 'برق', 'گاز', 'تلفن', 'اینترنت', 'اینترانت', 'شبکه دولت',
  'شارژ پیامک', 'سایر', 'دیتا'];
export const IDENTITY_TYPES = ['شماره اشتراک', 'شماره شناسایی', 'شماره پرونده'];

/** پوستهٔ تمام‌صفحه برای فرم‌های فرعی که از فرم اصلی باز می‌شوند */
export function SubFormShell({ children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: '#f0f2f7', overflow: 'hidden' }}>
      {children}
    </div>
  );
}

/** پیام ساده با یک دکمه */
export function MessageBox({ title = 'پیام', lines = [], onClose }) {
  const okRef = useRef(null);
  useEffect(() => { if (okRef.current) okRef.current.focus(); }, []);
  return (
    <div style={{ ...ui.overlay, zIndex: 3000 }} onClick={onClose}>
      <div style={{ ...ui.modal, maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>{title}</div>
          <button onClick={onClose} style={ui.closeBtn}>✕</button>
        </div>
        <div style={{ ...ui.mBody, lineHeight: 2, fontSize: '13px' }}>
          {lines.map((l, i) => <div key={i}>{l || ' '}</div>)}
        </div>
        <div style={ui.mFooter}>
          <button ref={okRef} onClick={onClose} style={ui.btnSave}>تایید</button>
        </div>
      </div>
    </div>
  );
}

/** کادر «انتخاب کنید» برای انتخاب بین چند گزینه */
export function ChoiceBox({ title = 'انتخاب کنید', text = '', options = [], onPick, onCancel }) {
  return (
    <div style={{ ...ui.overlay, zIndex: 3000 }} onClick={onCancel}>
      <div style={{ ...ui.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>{title}</div>
          <button onClick={onCancel} style={ui.closeBtn}>✕</button>
        </div>
        {text && <div style={{ ...ui.mBody, lineHeight: 2, fontSize: '13px', whiteSpace: 'pre-line' }}>{text}</div>}
        <div style={{ ...ui.mFooter, justifyContent: 'center' }}>
          {options.map(o => <button key={o} onClick={() => onPick(o)} style={ui.btnSave}>{o}</button>)}
        </div>
      </div>
    </div>
  );
}

export const splitLines = m => String(m || '').split('\n');

/** فیلد ساده */
export function Field({ label, value, onChange, flex = 1, disabled, onKeyDown, ltr }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, minWidth: '120px' }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value ?? ''} onChange={e => onChange && onChange(e.target.value)} disabled={disabled}
        onKeyDown={onKeyDown}
        style={{ ...ui.input, background: disabled ? '#f2f2f2' : '#fff', direction: ltr ? 'ltr' : 'rtl' }} />
    </div>
  );
}

/** جستجوی انتخابی — سرور {id, code, title, display} برمی‌گرداند */
export function Lookup({ label, value, endpoint, onPick, flex = 1, disabled }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (text) => {
    setQ(text);
    const j = await API(`${endpoint}${endpoint.includes('?') ? '&' : '?'}q=${encodeURIComponent(text)}`);
    if (j.ok) {
      setRows(j.rows || []);
      setOpen(true);
      if ((j.rows || []).length === 1 && text.trim()) { onPick(j.rows[0]); setOpen(false); setQ(''); }
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative', minWidth: '160px' }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value || q} disabled={disabled} placeholder="جستجو..."
        onChange={e => { if (!disabled) { if (value) onPick({ id: 0, title: '', display: '' }); search(e.target.value); } }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        style={{ ...ui.input, background: disabled ? '#f2f2f2' : '#fff' }} />
      {value && !disabled && (
        <button onClick={() => { onPick({ id: 0, title: '', display: '' }); setQ(''); }}
          style={{ position: 'absolute', left: '6px', top: '30px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828' }}>✕</button>
      )}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 700, maxHeight: '260px', overflowY: 'auto' }}>
          {rows.map(r => (
            <div key={r.id} onMouseDown={() => { onPick(r); setOpen(false); setQ(''); }}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#e8f0fe'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
              {r.display || r.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** جدول ساده با انتخاب ردیف؛ ستون‌ها: {key, label, num?, money?, date?} */
export function Grid({ columns, rows, selectedId, onSelect, onDouble, empty = 'موردی یافت نشد', loading, zebra = '#fdf6ec' }) {
  return (
    <>
      <table style={ui.table}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
          <tr>{columns.map(c => <th key={c.key} style={ui.th}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const sel = selectedId != null && selectedId === r.id;
            const bg = sel ? '#e8f0fe' : (i % 2 ? zebra : '#fff');
            return (
              <tr key={r.id ?? i} style={{ background: bg, cursor: 'pointer' }}
                onClick={() => onSelect && onSelect(r)} onDoubleClick={() => onDouble && onDouble(r)}>
                {columns.map(c => {
                  const v = r[c.key];
                  let t = v;
                  if (c.money) t = fmtAmt(v);
                  else if (c.num) t = fmtCnt(v);
                  else t = toFa(v ?? '') || '';
                  return (
                    <td key={c.key} title={String(v ?? '')}
                      style={{ ...(c.money || c.num ? ui.tdNum : ui.td), textAlign: c.money ? 'left' : (c.wrap ? 'right' : 'center'),
                        maxWidth: c.wrap ? '260px' : undefined, overflow: 'hidden', textOverflow: 'ellipsis',
                        fontWeight: sel ? 700 : undefined }}>
                      {t}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!loading && rows.length === 0 && <div style={ui.empty}>{empty}</div>}
    </>
  );
}

/** ستون‌هایی که در همهٔ ردیف‌ها خالی‌اند نشان داده نمی‌شوند */
export const visibleCols = (cols, rows) => cols.filter(c => c.always || rows.some(r => {
  const v = r[c.key];
  return v !== '' && v !== null && v !== undefined && v !== 0 && v !== '0';
}));

/** اجرای گزارش؛ خطا با onError برمی‌گردد */
export async function runReport(key, params, onError) {
  try {
    await openReport(key, params);
  } catch (e) {
    if (onError) onError(e.message || 'خطا در تهیه گزارش');
  }
}

/** کادر «تغییر موارد استفاده» */
export function ChangeUsesDialog({ info, targetsEndpoint, onMove, onClose, notChosenMsg }) {
  const [target, setTarget] = useState({ id: 0, display: '' });
  const [thisYear, setThisYear] = useState(true);
  const [err, setErr] = useState('');
  const doMove = () => {
    if (!target.id) { setErr(notChosenMsg); return; }
    if ((thisYear && !info.cnt_year) || (!thisYear && !info.cnt)) { setErr('تعداد موارد استفاده صفر است'); return; }
    onMove(target.id, thisYear);
  };
  return (
    <div style={ui.overlay} onClick={onClose}>
      <div style={{ ...ui.modal, maxWidth: '560px', overflow: 'visible' }} onClick={e => e.stopPropagation()}>
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>{info.caption}</div>
          <button onClick={onClose} style={ui.closeBtn}>✕</button>
        </div>
        <div style={{ ...ui.mBody, overflow: 'visible' }}>
          {err && <div style={ui.errBox}>⚠️ {err}</div>}
          <div style={ui.row}>
            <label style={{ fontSize: '13px' }}>
              <input type="radio" checked={thisYear} onChange={() => setThisYear(true)} /> {`سال ${toFa(info.year)}`}
              {' '}({toFa(info.cnt_year)})
            </label>
            <label style={{ fontSize: '13px' }}>
              <input type="radio" checked={!thisYear} onChange={() => setThisYear(false)} /> کلیه اطلاعات
              {' '}({toFa(info.cnt)})
            </label>
          </div>
          <div style={ui.row}>
            <Lookup label="به" value={target.display} endpoint={targetsEndpoint} onPick={setTarget} />
          </div>
        </div>
        <div style={ui.mFooter}>
          <button onClick={onClose} style={ui.btnCancel}>لغو</button>
          <button onClick={doMove} style={ui.btnSave}>انتقال</button>
        </div>
      </div>
    </div>
  );
}
